'use client';

import { useState, useRef } from 'react';
import Link from 'next/link';

import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: number;
  name: string;
  location: string;
  rating: number;
  text: string;
  service: string;
  image: string;
  alt: string;
}

interface TestimonialsSectionProps {
  className?: string;
}

const TestimonialsSection = ({ className = '' }: TestimonialsSectionProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const testimonials: Testimonial[] = [
  {
    id: 1,
    name: 'Sarah M',
    location: 'WestVancouver',
    rating: 5,
    text: 'Crystal Clean has been maintaining our home for three years now. They show up exactly when scheduled, do meticulous work, and treat our property with genuine care. It\'s rare to find a service provider you can truly trust.',
    service: 'Gutter & Window Cleaning',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_116022a91-1763299783599.png",
    alt: 'Professional woman with shoulder-length brown hair smiling warmly in business casual attire'
  },
  {
    id: 2,
    name: 'Michael Chen',
    location: 'Riverside',
    rating: 5,
    text: 'After trying several cleaning services, we finally found Crystal Clean. The difference is night and day—they\'re professional, thorough, and genuinely care about doing quality work. Our home looks better than it has in years.',
    service: 'Exterior Washing & Roof Cleaning',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_106e6bb30-1763294201797.png",
    alt: 'Asian man in his forties with short black hair wearing navy blue polo shirt smiling confidently'
  },
  {
    id: 3,
    name: 'Jennifer Rodriguez',
    location: 'Maple Grove',
    rating: 5,
    text: 'What I appreciate most is their consistency. Every visit is the same high quality, and they communicate clearly about what they\'re doing and why. They\'ve become our trusted partner in home maintenance.',
    service: 'Complete Maintenance Plan',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fc758cda-1763296884589.png",
    alt: 'Hispanic woman with long dark hair in professional attire smiling brightly outdoors'
  },
  {
    id: 4,
    name: 'David Thompson',
    location: 'Hillside Estates',
    rating: 5,
    text: 'We\'ve been customers for five years and wouldn\'t trust anyone else with our home\'s exterior. Crystal Clean\'s consistency and reliability are unmatched. They remember our preferences, communicate proactively, and deliver exceptional results every single time.',
    service: 'Exterior Washing',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1adc89ed4-1763294445054.png",
    alt: 'Caucasian man with gray hair and glasses wearing blue button-down shirt outdoors'
  },
  {
    id: 5,
    name: 'Lisa Anderson',
    location: 'Sunset Valley',
    rating: 5,
    text: 'As a busy professional, I need service providers I can trust completely. Crystal Clean has never let me down. They handle everything professionally, send reminders for seasonal maintenance, and their work is always thorough and careful.',
    service: 'Gutter Cleaning',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cdcb4aa6-1767165720112.png",
    alt: 'Blonde woman with wavy hair wearing casual sweater smiling in natural lighting'
  },
  {
    id: 6,
    name: 'Robert Williams',
    location: 'Cedar Park',
    rating: 5,
    text: 'After retirement, maintaining our home became challenging. Crystal Clean has been a godsend—they\'re patient, explain everything clearly, and treat our property like it\'s their own. The quality of their work reflects their genuine pride in what they do.',
    service: 'Window Cleaning',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18900111e-1766772615459.png",
    alt: 'Senior man with white hair wearing casual polo shirt smiling confidently'
  }];

  // Carousel navigation functions
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  // Touch handlers for swipe functionality
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 75) {
      // Swiped left
      nextSlide();
    }

    if (touchStart - touchEnd < -75) {
      // Swiped right
      prevSlide();
    }
  };

  return (
    <section className={`py-20 bg-surface ${className}`}>
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="font-serif text-4xl lg:text-5xl font-semibold text-foreground mb-6">
            What Our Customers Say
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Don't just take our word for it. Here's what homeowners who value quality and reliability have to say about their experience with Crystal Clean.
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative mb-12 max-w-4xl mx-auto">
          <div
            ref={carouselRef}
            className="overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}>

            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}>

              {testimonials.map((testimonial) =>
              <div key={testimonial.id} className="w-full flex-shrink-0 px-4">
                  <div className="max-w-2xl mx-auto bg-white rounded-xl p-8 shadow-brand hover:shadow-brand-lg transition-smooth">
                    <div className="flex items-center space-x-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, index) =>
                    <Icon key={index} name="StarIcon" size={20} className="text-warning" variant="solid" />
                    )}
                    </div>
                    
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      "{testimonial.text}"
                    </p>
                    
                    <div className="flex items-center space-x-4 pt-6 border-t border-border">
                      <div>
                        <p className="font-semibold text-foreground">{testimonial.name}</p>
                        <p className="text-xs text-primary mt-1">{testimonial.service}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Navigation Arrows */}
          {testimonials.length > 1 &&
          <>
              <button
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white hover:bg-sky-100 text-primary p-3 rounded-full shadow-lg transition-all hover:scale-110 z-10"
              aria-label="Previous testimonial">

                <Icon name="ChevronLeftIcon" size={24} />
              </button>
              <button
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white hover:bg-sky-100 text-primary p-3 rounded-full shadow-lg transition-all hover:scale-110 z-10"
              aria-label="Next testimonial">

                <Icon name="ChevronRightIcon" size={24} />
              </button>
            </>
          }

          {/* Dot Indicators */}
          {testimonials.length > 1 &&
          <div className="flex justify-center items-center space-x-2 mt-8">
              {testimonials.map((_, index) =>
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all ${
              currentSlide === index ?
              'w-8 h-3 bg-primary rounded-full' : 'w-3 h-3 bg-sky-300 rounded-full hover:bg-sky-400'}`
              }
              aria-label={`Go to testimonial ${index + 1}`} />

            )}
            </div>
          }
        </div>

        <div className="text-center">
          <Link
            href="/customer-reviews"
            className="inline-flex items-center justify-center px-8 py-4 bg-white text-foreground font-semibold rounded-full hover:bg-accent transition-smooth shadow-brand-sm btn-bubbly">

            Read More Reviews
            <Icon name="ArrowRightIcon" size={20} className="ml-2" />
          </Link>
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;